import { defineComponent as _defineComponent } from 'vue'
import { toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createTextVNode as _createTextVNode, normalizeClass as _normalizeClass, withModifiers as _withModifiers, normalizeStyle as _normalizeStyle, createBlock as _createBlock, vModelText as _vModelText, withKeys as _withKeys, withDirectives as _withDirectives } from "vue"

const _hoisted_1 = { class: "social-message-composer" }
const _hoisted_2 = {
  key: 0,
  class: "social-message-composer__reply-tag"
}
const _hoisted_3 = { class: "social-message-composer__reply-tag-text" }
const _hoisted_4 = { class: "social-message-composer__reply-tag-title" }
const _hoisted_5 = { class: "social-message-composer__reply-tag-snippet" }
const _hoisted_6 = {
  key: 1,
  class: "social-message-composer__previews"
}
const _hoisted_7 = { class: "social-message-composer__preview-info" }
const _hoisted_8 = { class: "social-message-composer__preview-name" }
const _hoisted_9 = { class: "social-message-composer__preview-size" }
const _hoisted_10 = ["onClick"]
const _hoisted_11 = {
  key: 0,
  class: "social-message-composer__preview-item"
}
const _hoisted_12 = { class: "social-message-composer__preview-info" }
const _hoisted_13 = { class: "social-message-composer__preview-name" }
const _hoisted_14 = { class: "social-message-composer__preview-size" }
const _hoisted_15 = ["src"]
const _hoisted_16 = {
  key: 1,
  class: "social-message-composer__preview-item"
}
const _hoisted_17 = { class: "social-message-composer__preview-info" }
const _hoisted_18 = { class: "social-message-composer__preview-name" }
const _hoisted_19 = { class: "social-message-composer__preview-size" }
const _hoisted_20 = ["src"]
const _hoisted_21 = {
  key: 2,
  class: "social-message-composer__video-live-preview"
}
const _hoisted_22 = { class: "social-message-composer__video-live-badge" }
const _hoisted_23 = {
  key: 0,
  class: "social-message-composer__ai-buttons-wrapper"
}
const _hoisted_24 = ["disabled", "title"]
const _hoisted_25 = ["disabled", "title"]
const _hoisted_26 = ["disabled", "title"]
const _hoisted_27 = ["disabled", "title"]
const _hoisted_28 = { class: "social-message-composer__editor-wrapper" }
const _hoisted_29 = { class: "social-message-composer__footer" }
const _hoisted_30 = { class: "social-message-composer__footer-left" }
const _hoisted_31 = ["disabled"]
const _hoisted_32 = {
  key: 1,
  class: "social-message-composer__recording-time"
}
const _hoisted_33 = ["disabled"]
const _hoisted_34 = {
  key: 3,
  class: "social-message-composer__recording-time"
}
const _hoisted_35 = { class: "social-message-composer__footer-inline-tools" }
const _hoisted_36 = { class: "social-message-composer__popover-content" }
const _hoisted_37 = { class: "social-message-composer__link-field-wrapper" }
const _hoisted_38 = { class: "social-message-composer__link-field-wrapper social-message-composer__link-field-wrapper--url" }
const _hoisted_39 = ["disabled"]
const _hoisted_40 = { class: "social-message-composer__split-button" }
const _hoisted_41 = ["disabled"]
const _hoisted_42 = ["disabled"]

import ActionConfirmationModal from "@/components/modal/ActionConfirmationModal.vue";
import ScheduleSendModal from "@/components/socials/ScheduleSendModal.vue";
import RichTextEditor from "@/components/ui/RichTextEditor.vue";
import type { Conversation, Message } from "@/dtos/Unipile.dto";
import { getEditorHTML, getQuillInstance, isBodyEmpty } from "@/utils/emailComposerHelpers";
import { Icon } from "@iconify/vue";
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useToast } from "@/composables/ui/useToast";
import EmojiPicker from "./EmojiPicker.vue";
import AiRefinePopover from "./AiRefinePopover.vue";
import IcpPicker from "@/components/ui/IcpPicker.vue";
import AIGenerationService from "@/services/ai/ai-generation";
import { useIcpPicker } from "@/composables/ui/useIcpPicker";
import { useMessageDraft } from "@/composables/messages/useMessageDraft";
import type { MessageDraftChannel } from "@/dtos/MessageDraft.dto";

interface Props {
  conversation: Conversation | null;
  replyTo?: Message | null;
  channelStatus?: string;
  isSending?: boolean;
  showAiButtons?: boolean;
  aiButtonsDisabled?: boolean;
  isGeneratingAI?: boolean;
  generatingAction?: string | null;
  contactId?: string;
  icpId?: string;
  icps?: Array<{ id: string; name: string; isDefault?: boolean }>;
  channelName?: string;
}

const MIN_EDITOR_HEIGHT = 90;
const AI_BUTTONS_ROW_HEIGHT = 34;
const MAX_EDITOR_HEIGHT = 360;

export default /*@__PURE__*/_defineComponent({
  __name: 'SocialMessageComposer',
  props: {
    conversation: {},
    replyTo: {},
    channelStatus: {},
    isSending: { type: Boolean, default: false },
    showAiButtons: { type: Boolean, default: false },
    aiButtonsDisabled: { type: Boolean, default: false },
    isGeneratingAI: { type: Boolean, default: false },
    generatingAction: {},
    contactId: {},
    icpId: {},
    icps: {},
    channelName: {}
  },
  emits: ["send-message", "schedule-send", "cancel-reply", "ai-reply", "ai-followup", "ai-compose"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

const props = __props;

const $toast = useToast();

const emit = __emit;

const richTextEditor = ref<InstanceType<typeof RichTextEditor> | null>(null);
const messageContent = ref("");

const draftChannel = (props.channelName ||
  props.conversation?.channel ||
  "linkedin") as MessageDraftChannel;
const { fetchDraft, scheduleSave, flushSave, clearDraft } = useMessageDraft(draftChannel);
let loadedDraftConversationId = "";
const showEmojiPopover = ref(false);
const showLinkPopover = ref(false);
const linkText = ref("");
const linkUrl = ref("");
const linkInputRef = ref<HTMLInputElement | null>(null);
const linkPopoverRef = ref<HTMLElement | null>(null);
const linkPopoverWrapperRef = ref<HTMLElement | null>(null);
const sendButtonRef = ref<HTMLElement | null>(null);
const popoverStyle = ref<{ bottom?: string; right?: string; left?: string }>({});

const attachments = ref<File[]>([]);

// Voice recording
const isRecording = ref(false);
const recordingTime = ref("00:00");
const voiceMessage = ref<File | null>(null);
let mediaRecorder: MediaRecorder | null = null;
let audioStream: MediaStream | null = null;
let audioChunks: Blob[] = [];
let recordingStart = 0;
let recordingTimer: number | null = null;

// Video recording
const isVideoRecording = ref(false);
const videoRecordingTime = ref("00:00");
const videoMessage = ref<File | null>(null);
const liveVideoRef = ref<HTMLVideoElement | null>(null);
let videoMediaRecorder: MediaRecorder | null = null;
let videoStream: MediaStream | null = null;
let videoChunks: Blob[] = [];
let videoRecordingStart = 0;
let videoRecordingTimer: number | null = null;
let videoAutoStopTimeout: number | null = null;

const showScheduleModal = ref(false);
const showScheduleMenu = ref(false);
const isScheduling = ref(false);
const showAiPanel = ref(false);
const showAiComposePanel = ref(false);
const isGeneratingAiCustom = ref(false);
const aiRefineButtonRef = ref<HTMLElement | null>(null);
const aiComposeButtonRef = ref<HTMLElement | null>(null);
const pendingComposeIcpId = ref<string | undefined>(undefined);

// Editor resize (drag the handle above the AI buttons to grow/shrink the message box)
const minResizableHeight = computed(
  () => MIN_EDITOR_HEIGHT + (props.showAiButtons ? AI_BUTTONS_ROW_HEIGHT : 0)
);
const editorHeight = ref(MIN_EDITOR_HEIGHT + AI_BUTTONS_ROW_HEIGHT);
const isResizingEditor = ref(false);
let resizeStartY = 0;
let resizeStartHeight = 0;

const handleEditorResizeMove = (event: MouseEvent) => {
  if (!isResizingEditor.value) return;
  const delta = resizeStartY - event.clientY;
  editorHeight.value = Math.min(
    MAX_EDITOR_HEIGHT,
    Math.max(minResizableHeight.value, resizeStartHeight + delta)
  );
};

const stopEditorResize = () => {
  if (!isResizingEditor.value) return;
  isResizingEditor.value = false;
  window.removeEventListener("mousemove", handleEditorResizeMove);
  window.removeEventListener("mouseup", stopEditorResize);
  document.body.style.cursor = "";
  document.body.style.userSelect = "";
};

const startEditorResize = (event: MouseEvent) => {
  event.preventDefault();
  isResizingEditor.value = true;
  resizeStartY = event.clientY;
  resizeStartHeight = editorHeight.value;
  window.addEventListener("mousemove", handleEditorResizeMove);
  window.addEventListener("mouseup", stopEditorResize);
  document.body.style.cursor = "ns-resize";
  document.body.style.userSelect = "none";
};

const supportsVoiceMessage = computed(() => {
  const channel = props.conversation?.channel;
  return channel === "whatsapp" || channel === "linkedin";
});

const supportsVideoMessage = computed(() => {
  const channel = props.conversation?.channel;
  return channel === "whatsapp" || channel === "linkedin";
});

const replyTo = computed(() => props.replyTo || null);

const replyToDisplayName = computed(() => {
  if (!props.replyTo) return "";
  if (props.replyTo.isOwn) return "You";
  return props.replyTo.senderName || "Unknown";
});

const stripHtml = (html: string) => html.replace(/<[^>]*>/g, "");

const replyToSnippet = computed(() => {
  if (!props.replyTo) return "";
  const raw = (props.replyTo.text || "").toString();
  const cleaned = stripHtml(raw).replace(/\s+/g, " ").trim();
  if (!cleaned) return " ";
  return cleaned.length > 90 ? cleaned.slice(0, 90) + "…" : cleaned;
});

const isStatusGood = computed(() => {
  if (!props.channelStatus) return false;
  const status = props.channelStatus.toUpperCase();
  return ["OK", "CREATION_SUCCESS", "RECONNECTED", "SYNC_SUCCESS"].includes(status);
});

const canSend = computed(() => {
  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
  const hasValidContent = htmlContent && !isBodyEmpty(htmlContent);
  const hasAttachments = attachments.value.length > 0;
  const hasVoice = !!voiceMessage.value;
  const hasVideo = !!videoMessage.value;

  if (!hasAttachments && !hasVoice && !hasVideo) {
    return hasValidContent && isStatusGood.value && !isRecording.value && !isVideoRecording.value;
  }

  return (
    (hasValidContent || hasAttachments || hasVoice || hasVideo) &&
    isStatusGood.value &&
    !isRecording.value &&
    !isVideoRecording.value
  );
});

const getQuill = () => getQuillInstance(richTextEditor.value as any);

const toggleLinkPopover = () => {
  if (showLinkPopover.value) {
    closeLinkPopover();
  } else {
    closeAllPopovers();

    const quill = getQuill();
    if (quill) {
      const range = quill.getSelection(true);
      if (range && range.length > 0) {
        const selectedText = quill.getText(range.index, range.length);
        linkText.value = selectedText.trim();
      }
    }

    showLinkPopover.value = true;
    nextTick(() => {
      if (linkPopoverWrapperRef.value) {
        const wrapperRect = linkPopoverWrapperRef.value.getBoundingClientRect();
        const popoverWidth = 240;
        const spacing = 8;
        const viewportWidth = window.innerWidth;
        const viewportHeight = window.innerHeight;
        let right: string | undefined = undefined;
        let left: string | undefined = undefined;
        const rightPosition = viewportWidth - wrapperRect.right;

        if (wrapperRect.right - popoverWidth < 16) {
          left = "16px";
          right = undefined;
        } else {
          right = `${rightPosition}px`;
          left = undefined;
        }

        const bottom = `${viewportHeight - wrapperRect.top + spacing}px`;

        popoverStyle.value = {
          bottom,
          right,
          left,
        };
      }

      if (linkInputRef.value) {
        linkInputRef.value.focus();
      }
    });
  }
};

const closeLinkPopover = () => {
  showLinkPopover.value = false;
  linkUrl.value = "";
  linkText.value = "";
};

const insertLink = () => {
  const quill = getQuill();
  if (!quill) return;

  if (!linkUrl.value || linkUrl.value.trim() === "") {
    return;
  }

  try {
    const range = quill.getSelection(true);
    if (!range) {
      closeLinkPopover();
      return;
    }

    let url = linkUrl.value.trim();
    if (!url.startsWith("http://") && !url.startsWith("https://")) {
      url = "https://" + url;
    }

    const text = linkText.value.trim() || url;

    if (range.length > 0) {
      quill.formatText(range.index, range.length, "link", url);
    } else {
      quill.insertText(range.index, text, "link", url);
      quill.setSelection(range.index + text.length);
    }

    closeLinkPopover();
  } catch {
    closeLinkPopover();
  }
};

const toggleEmojiPopover = () => {
  if (showEmojiPopover.value) {
    showEmojiPopover.value = false;
  } else {
    closeAllPopovers();
    showEmojiPopover.value = true;
  }
};

const closeEmojiPopover = () => {
  showEmojiPopover.value = false;
};

const handleEmojiSelect = (emoji: any) => {
  const quill = getQuill();
  if (!quill) {
    closeEmojiPopover();
    return;
  }

  try {
    const range = quill.getSelection(true);
    const index = range ? range.index : quill.getLength() - 1;

    const emojiChar = emoji.emoji || emoji.native || emoji;
    quill.insertText(index, emojiChar);
    quill.setSelection(index + emojiChar.length);

    closeEmojiPopover();
  } catch {
    closeEmojiPopover();
  }
};

const handleAttachment = (type: string) => {
  if (type === "voice") {
    if (!supportsVoiceMessage.value) return;
    if (isRecording.value) {
      stopRecording();
    } else {
      startRecording();
    }
    return;
  }

  const input = document.createElement("input");
  input.type = "file";
  input.multiple = true;

  if (type === "photo") {
    input.accept = "image/*";
  } else if (type === "video") {
    input.accept = "video/*";
  } else {
    input.accept = "*/*";
  }

  input.onchange = async (e: Event) => {
    const target = e.target as HTMLInputElement;
    const files = Array.from(target.files || []);
    if (!files.length) return;

    if (!validateFiles(files)) {
      target.value = "";
      return;
    }

    addAttachments(files);
    target.value = "";
  };

  input.click();
};

const validateFiles = (files: File[]) => {
  for (const file of files) {
    if (file.size > 16 * 1024 * 1024) {
      $toast?.error?.(`File "${file.name}" is too large. Maximum size is 16MB.`);
      return false;
    }
  }
  return true;
};

const addAttachments = (files: File[]) => {
  attachments.value.push(...files);
};

const removeAttachment = (index: number) => {
  attachments.value.splice(index, 1);
};

const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return "0 Bytes";
  const k = 1024;
  const sizes = ["Bytes", "KB", "MB", "GB"];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + " " + sizes[i];
};

const getFileIcon = (mimeType: string): string => {
  if (mimeType.startsWith("image/")) return "mdi:image-outline";
  if (mimeType.startsWith("video/")) return "mdi:video-outline";
  if (mimeType.includes("pdf")) return "mdi:file-pdf-box";
  if (mimeType.includes("word") || mimeType.includes("document")) return "mdi:file-word";
  if (mimeType.includes("excel") || mimeType.includes("spreadsheet")) return "mdi:file-excel";
  return "mdi:file-outline";
};

const voiceMessageUrl = computed((): string | undefined => {
  if (!voiceMessage.value) return undefined;
  return URL.createObjectURL(voiceMessage.value);
});

const videoMessageUrl = computed((): string | undefined => {
  if (!videoMessage.value) return undefined;
  return URL.createObjectURL(videoMessage.value);
});

const closeAllPopovers = () => {
  showLinkPopover.value = false;
  showEmojiPopover.value = false;
};

const startRecording = async () => {
  try {
    audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    mediaRecorder = new MediaRecorder(audioStream, { mimeType: "audio/webm" });
    audioChunks.length = 0;

    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size) audioChunks.push(e.data);
    };

    mediaRecorder.onstop = () => {
      if (!audioChunks.length) return;
      const blob = new Blob(audioChunks, { type: "audio/webm" });
      const fileName = `voice_message_${Date.now()}.webm`;
      const file = new File([blob], fileName, { type: "audio/webm" });

      voiceMessage.value = file;
    };

    recordingStart = Date.now();
    isRecording.value = true;
    recordingTime.value = "00:00";
    recordingTimer = window.setInterval(() => {
      const elapsed = Date.now() - recordingStart;
      const sec = Math.floor(elapsed / 1000);
      const min = Math.floor(sec / 60);
      const s = sec % 60;
      recordingTime.value = `${String(min).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, 1000);

    mediaRecorder.start();
  } catch (err) {
    console.error("Error starting recording:", err);
    $toast?.error?.("Unable to access microphone.");
    isRecording.value = false;
  }
};

const stopRecording = () => {
  if (!isRecording.value) return;
  isRecording.value = false;
  if (recordingTimer !== null) {
    clearInterval(recordingTimer);
    recordingTimer = null;
  }
  if (mediaRecorder && mediaRecorder.state !== "inactive") {
    mediaRecorder.stop();
  }
  if (audioStream) {
    audioStream.getTracks().forEach((t) => t.stop());
    audioStream = null;
  }
};

const resetVoice = () => {
  if (isRecording.value) stopRecording();
  voiceMessage.value = null;
  recordingTime.value = "00:00";
};

const startVideoRecording = async () => {
  if (videoStream) {
    videoStream.getTracks().forEach((t) => t.stop());
    videoStream = null;
  }
  try {
    videoStream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });

    const mimeTypes = ["video/mp4", "video/webm;codecs=h264", "video/webm"];
    const mimeType = mimeTypes.find((t) => MediaRecorder.isTypeSupported(t)) || "video/webm";
    const ext = mimeType.startsWith("video/mp4") ? "mp4" : "webm";

    videoMediaRecorder = new MediaRecorder(videoStream, { mimeType });
    videoChunks = [];

    videoMediaRecorder.ondataavailable = (e) => {
      if (e.data.size) videoChunks.push(e.data);
    };

    videoMediaRecorder.onstop = () => {
      if (!videoChunks.length) return;
      const blob = new Blob(videoChunks, { type: mimeType });
      const fileName = `video_message_${Date.now()}.${ext}`;
      videoMessage.value = new File([blob], fileName, { type: mimeType });
    };

    videoRecordingStart = Date.now();
    isVideoRecording.value = true;
    videoRecordingTime.value = "00:00";
    nextTick(() => {
      if (liveVideoRef.value) liveVideoRef.value.srcObject = videoStream;
    });
    videoRecordingTimer = window.setInterval(() => {
      const elapsed = Date.now() - videoRecordingStart;
      const sec = Math.floor(elapsed / 1000);
      const min = Math.floor(sec / 60);
      const s = sec % 60;
      videoRecordingTime.value = `${String(min).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
    }, 1000);

    videoAutoStopTimeout = window.setTimeout(() => stopVideoRecording(), 60000);

    videoMediaRecorder.start();
  } catch (err) {
    console.error("Error starting video recording:", err);
    $toast?.error?.("Unable to access camera.");
    isVideoRecording.value = false;
    if (videoStream) {
      videoStream.getTracks().forEach((t) => t.stop());
      videoStream = null;
    }
  }
};

const stopVideoRecording = () => {
  if (!isVideoRecording.value) return;
  isVideoRecording.value = false;
  if (videoRecordingTimer !== null) {
    clearInterval(videoRecordingTimer);
    videoRecordingTimer = null;
  }
  if (videoAutoStopTimeout !== null) {
    clearTimeout(videoAutoStopTimeout);
    videoAutoStopTimeout = null;
  }
  if (videoMediaRecorder && videoMediaRecorder.state !== "inactive") {
    videoMediaRecorder.stop();
  }
  if (videoStream) {
    videoStream.getTracks().forEach((t) => t.stop());
    videoStream = null;
  }
};

const resetVideo = () => {
  if (isVideoRecording.value) stopVideoRecording();
  videoMessage.value = null;
  videoRecordingTime.value = "00:00";
};

const handleSend = () => {
  if (!canSend.value || !props.conversation) return;

  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
  const hasAttachments = attachments.value.length > 0;
  const hasVoice = !!voiceMessage.value;

  const cleanedContent = isBodyEmpty(htmlContent) ? "" : htmlContent;

  const sendData: {
    text?: string;
    quote_id?: string;
    attachments?: File[];
    voice_message?: File;
    video_message?: File;
  } = {};

  if (cleanedContent || (!hasAttachments && !hasVoice)) {
    sendData.text = cleanedContent;
  }

  if (props.replyTo?.id) {
    sendData.quote_id = props.replyTo.id;
  }

  if (attachments.value.length) {
    sendData.attachments = [...attachments.value];
  }

  if (voiceMessage.value) {
    sendData.voice_message = voiceMessage.value;
  }

  if (videoMessage.value) {
    sendData.video_message = videoMessage.value;
  }

  emit("send-message", sendData);
  clearDraft(props.conversation.id);
  messageContent.value = "";
  attachments.value = [];
  resetVoice();
  resetVideo();
  showEmojiPopover.value = false;
};

const buildScheduledPayload = (scheduledAt: string) => {
  const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);

  const data: {
    text: string;
    quote_id?: string;
    attachments?: File[];
    voice_message?: File;
    video_message?: File;
    scheduledAt: string;
  } = {
    text: htmlContent,
    scheduledAt,
  };

  if (props.replyTo?.id) {
    data.quote_id = props.replyTo.id;
  }

  if (attachments.value.length) {
    data.attachments = attachments.value;
  }

  if (voiceMessage.value) {
    data.voice_message = voiceMessage.value;
  }

  if (videoMessage.value) {
    data.video_message = videoMessage.value;
  }

  return data;
};

const toggleScheduleMenu = () => {
  showScheduleMenu.value = !showScheduleMenu.value;
  if (showScheduleMenu.value) {
    closeAllPopovers();
  }
};

const handleScheduleMenuClick = () => {
  showScheduleMenu.value = false;
  showScheduleModal.value = true;
};

const showAiActionConfirm = ref(false);
const pendingAiActionMode = ref<"reply" | "followup" | null>(null);
const pendingAiActionIcpId = ref<string | undefined>(undefined);

const aiActionConfirmTitle = computed(() =>
  pendingAiActionMode.value === "followup" ? "Generate AI Follow-up" : "Generate AI Reply"
);
const aiActionConfirmMessage = computed(() =>
  pendingAiActionMode.value === "followup"
    ? "This will generate an AI-written follow-up message. Continue?"
    : "This will generate an AI-written reply. Continue?"
);

const confirmAiAction = () => {
  if (pendingAiActionMode.value === "reply") {
    emit("ai-reply", { icpId: pendingAiActionIcpId.value });
  } else if (pendingAiActionMode.value === "followup") {
    emit("ai-followup", { icpId: pendingAiActionIcpId.value });
  }
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const cancelAiAction = () => {
  showAiActionConfirm.value = false;
  pendingAiActionMode.value = null;
};

const { showIcpPicker, icpPickerMode, triggerAiAction, selectIcp, dismissIcpPicker } = useIcpPicker(
  (mode, icpId) => {
    if (mode === "reply" || mode === "followup") {
      pendingAiActionMode.value = mode;
      pendingAiActionIcpId.value = icpId;
      showAiActionConfirm.value = true;
    } else {
      pendingComposeIcpId.value = icpId;
      showAiPanel.value = false;
      showAiComposePanel.value = true;
    }
  }
);

const toggleAiRefinePanel = () => {
  showAiComposePanel.value = false;
  showAiPanel.value = !showAiPanel.value;
};

const handleAiReply = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("reply", props.icps);
};

const handleAiFollowup = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("followup", props.icps);
};

const handleAiCompose = () => {
  if (props.aiButtonsDisabled || props.isGeneratingAI) return;
  triggerAiAction("compose", props.icps);
};

const handleAiComposeGenerate = (payload: { customInstructions: string }) => {
  emit("ai-compose", {
    icpId: pendingComposeIcpId.value,
    customInstructions: payload.customInstructions,
  });
  showAiComposePanel.value = false;
};

const handleAiCustomizationGenerate = async (payload: {
  customInstructions: string;
  attachments?: File[];
}) => {
  if (isGeneratingAiCustom.value) return;
  isGeneratingAiCustom.value = true;
  try {
    const response = await AIGenerationService.generateRefine({
      contactId: props.contactId || "",
      icpId: props.icpId,
      channel: props.channelName || props.conversation?.channel || "",
      currentDraft: messageContent.value,
      customInstructions: payload.customInstructions,
      files: payload.attachments,
    });
    setContent(response.body);
    showAiPanel.value = false;
    $toast?.success("AI message refined");
  } catch (error) {
    console.error("Failed to refine AI message:", error);
    $toast?.error("Failed to refine message");
  } finally {
    isGeneratingAiCustom.value = false;
  }
};

const handleScheduleSend = async (scheduleData: { scheduledAt: string }) => {
  if (isScheduling.value) {
    return;
  }

  try {
    isScheduling.value = true;
    const payload = buildScheduledPayload(scheduleData.scheduledAt);
    emit("schedule-send", payload);

    setTimeout(() => {
      showScheduleModal.value = false;
      attachments.value = [];
      resetVoice();
      resetVideo();
      isScheduling.value = false;
    }, 500);
  } catch (error) {
    console.error("Error scheduling send:", error);
    isScheduling.value = false;
  }
};

watch(
  () => props.replyTo,
  () => {
    if (!props.replyTo) {
      messageContent.value = "";
    }
  }
);

watch(showScheduleModal, (newVal) => {
  if (!newVal) {
    isScheduling.value = false;
  }
});

const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement;

  if (
    showEmojiPopover.value &&
    !target.closest(".social-message-composer__emoji-container") &&
    !target.closest("[title='Insert emoji']")
  ) {
    closeEmojiPopover();
  }

  if (showLinkPopover.value && linkPopoverRef.value && !linkPopoverRef.value.contains(target)) {
    closeLinkPopover();
  }

  if (showScheduleMenu.value && sendButtonRef.value && !sendButtonRef.value.contains(target)) {
    showScheduleMenu.value = false;
  }
};

if (typeof window !== "undefined") {
  document.addEventListener("click", handleClickOutside);
}

watch(voiceMessageUrl, (newUrl, oldUrl) => {
  if (oldUrl) {
    URL.revokeObjectURL(oldUrl);
  }
});

watch(videoMessageUrl, (newUrl, oldUrl) => {
  if (oldUrl) {
    URL.revokeObjectURL(oldUrl);
  }
});

onBeforeUnmount(() => {
  if (voiceMessageUrl.value) {
    URL.revokeObjectURL(voiceMessageUrl.value);
  }
  if (videoMessageUrl.value) {
    URL.revokeObjectURL(videoMessageUrl.value);
  }
  resetVideo();
  if (props.conversation?.id) {
    const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
    flushSave(props.conversation.id, isBodyEmpty(htmlContent) ? "" : htmlContent);
  }
  if (typeof window !== "undefined") {
    document.removeEventListener("click", handleClickOutside);
    window.removeEventListener("mousemove", handleEditorResizeMove);
    window.removeEventListener("mouseup", stopEditorResize);
  }
});

const setContent = (content: string) => {
  messageContent.value = content;
  nextTick(() => {
    if (richTextEditor.value) {
      const quill = getQuillInstance(richTextEditor.value as any);
      if (quill) {
        quill.root.innerHTML = content;
      }
    }
  });
};

let currentDraftConversationId = "";

const loadDraftForConversation = async (conversationId: string) => {
  currentDraftConversationId = conversationId;
  setContent("");
  if (!conversationId) return;
  const draftContent = await fetchDraft(conversationId);
  if (currentDraftConversationId === conversationId && draftContent) {
    setContent(draftContent);
  }
};

onMounted(() => {
  loadDraftForConversation(props.conversation?.id || "");
});

watch(
  () => props.conversation?.id,
  (newId, oldId) => {
    if (oldId) {
      const htmlContent = getEditorHTML(richTextEditor.value as any, messageContent.value);
      flushSave(oldId, isBodyEmpty(htmlContent) ? "" : htmlContent);
    }
    loadDraftForConversation(newId || "");
  }
);

watch(messageContent, (newVal) => {
  const conversationId = props.conversation?.id;
  if (!conversationId) return;
  const htmlContent = getEditorHTML(richTextEditor.value as any, newVal);
  scheduleSave(conversationId, isBodyEmpty(htmlContent) ? "" : htmlContent);
});

__expose({
  setContent,
  messageContent,
  richTextEditor,
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (replyTo.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [
          _createElementVNode("div", _hoisted_3, [
            _createElementVNode("div", _hoisted_4, " Reply to " + _toDisplayString(replyToDisplayName.value), 1),
            _createElementVNode("div", _hoisted_5, _toDisplayString(replyToSnippet.value), 1)
          ]),
          _createElementVNode("button", {
            type: "button",
            class: "social-message-composer__reply-tag-close",
            title: "Cancel reply",
            onClick: _cache[0] || (_cache[0] = ($event: any) => (emit('cancel-reply')))
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:close" })
          ])
        ]))
      : _createCommentVNode("", true),
    (attachments.value.length > 0 || voiceMessage.value || videoMessage.value || isVideoRecording.value)
      ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
          (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(attachments.value, (file, index) => {
            return (_openBlock(), _createElementBlock("div", {
              key: index,
              class: "social-message-composer__preview-item"
            }, [
              _createVNode(_unref(Icon), {
                icon: getFileIcon(file.type),
                class: "social-message-composer__preview-icon"
              }, null, 8, ["icon"]),
              _createElementVNode("div", _hoisted_7, [
                _createElementVNode("div", _hoisted_8, _toDisplayString(file.name), 1),
                _createElementVNode("div", _hoisted_9, _toDisplayString(formatFileSize(file.size)), 1)
              ]),
              _createElementVNode("button", {
                type: "button",
                class: "social-message-composer__preview-remove",
                onClick: ($event: any) => (removeAttachment(index)),
                title: "Remove file"
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:close" })
              ], 8, _hoisted_10)
            ]))
          }), 128)),
          (voiceMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_11, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:microphone",
                  class: "social-message-composer__preview-icon"
                }),
                _createElementVNode("div", _hoisted_12, [
                  _createElementVNode("div", _hoisted_13, _toDisplayString(voiceMessage.value.name), 1),
                  _createElementVNode("div", _hoisted_14, _toDisplayString(formatFileSize(voiceMessage.value.size)), 1)
                ]),
                (voiceMessageUrl.value)
                  ? (_openBlock(), _createElementBlock("audio", {
                      key: 0,
                      src: voiceMessageUrl.value,
                      controls: "",
                      class: "social-message-composer__preview-audio"
                    }, null, 8, _hoisted_15))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  class: "social-message-composer__preview-remove",
                  onClick: resetVoice,
                  title: "Remove voice message"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:close" })
                ])
              ]))
            : _createCommentVNode("", true),
          (videoMessage.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_16, [
                _createVNode(_unref(Icon), {
                  icon: "mdi:video-outline",
                  class: "social-message-composer__preview-icon"
                }),
                _createElementVNode("div", _hoisted_17, [
                  _createElementVNode("div", _hoisted_18, _toDisplayString(videoMessage.value.name), 1),
                  _createElementVNode("div", _hoisted_19, _toDisplayString(formatFileSize(videoMessage.value.size)), 1)
                ]),
                (videoMessageUrl.value)
                  ? (_openBlock(), _createElementBlock("video", {
                      key: 0,
                      src: videoMessageUrl.value,
                      controls: "",
                      class: "social-message-composer__preview-video"
                    }, null, 8, _hoisted_20))
                  : _createCommentVNode("", true),
                _createElementVNode("button", {
                  type: "button",
                  class: "social-message-composer__preview-remove",
                  onClick: resetVideo,
                  title: "Remove video message"
                }, [
                  _createVNode(_unref(Icon), { icon: "mdi:close" })
                ])
              ]))
            : _createCommentVNode("", true),
          (isVideoRecording.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_21, [
                _createElementVNode("video", {
                  ref_key: "liveVideoRef",
                  ref: liveVideoRef,
                  autoplay: "",
                  muted: "",
                  playsinline: "",
                  class: "social-message-composer__video-live"
                }, null, 512),
                _createElementVNode("div", _hoisted_22, [
                  _cache[12] || (_cache[12] = _createElementVNode("span", { class: "social-message-composer__video-live-dot" }, null, -1)),
                  _createTextVNode(" " + _toDisplayString(videoRecordingTime.value), 1)
                ])
              ]))
            : _createCommentVNode("", true)
        ]))
      : _createCommentVNode("", true),
    _createVNode(IcpPicker, {
      show: _unref(showIcpPicker),
      mode: _unref(icpPickerMode),
      icps: __props.icps,
      class: "social-message-composer__icp-picker-positioned",
      onSelect: _unref(selectIcp),
      onDismiss: _unref(dismissIcpPicker)
    }, null, 8, ["show", "mode", "icps", "onSelect", "onDismiss"]),
    _createElementVNode("div", {
      class: "social-message-composer__resizable-area",
      style: _normalizeStyle({ height: editorHeight.value + 'px' })
    }, [
      _createElementVNode("div", {
        class: _normalizeClass(["social-message-composer__resize-handle", { 'is-resizing': isResizingEditor.value }]),
        title: "Drag to resize",
        onMousedown: startEditorResize
      }, null, 34),
      (__props.showAiButtons)
        ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
            _createElementVNode("button", {
              ref_key: "aiComposeButtonRef",
              ref: aiComposeButtonRef,
              type: "button",
              class: "social-message-composer__ai-btn social-message-composer__ai-btn--compose",
              disabled: __props.aiButtonsDisabled || __props.isGeneratingAI,
              title: __props.isGeneratingAI ? 'Generating...' : 'AI Compose',
              onClick: _withModifiers(handleAiCompose, ["stop"])
            }, [
              (__props.isGeneratingAI && __props.generatingAction === 'compose')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[13] || (_cache[13] = _createElementVNode("span", { class: "social-message-composer__ai-spinner" }, null, -1)),
                    _cache[14] || (_cache[14] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "Generating...", -1))
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createVNode(_unref(Icon), { icon: "mdi:sparkles" }),
                    _cache[15] || (_cache[15] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "AI Compose", -1))
                  ], 64))
            ], 8, _hoisted_24),
            _createElementVNode("button", {
              type: "button",
              class: "social-message-composer__ai-btn social-message-composer__ai-btn--reply",
              disabled: __props.aiButtonsDisabled || __props.isGeneratingAI,
              title: __props.isGeneratingAI ? 'Generating...' : 'AI Reply',
              onClick: _withModifiers(handleAiReply, ["stop"])
            }, [
              (__props.isGeneratingAI && __props.generatingAction === 'reply')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[16] || (_cache[16] = _createElementVNode("span", { class: "social-message-composer__ai-spinner" }, null, -1)),
                    _cache[17] || (_cache[17] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "Generating...", -1))
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createVNode(_unref(Icon), { icon: "mdi:sparkles" }),
                    _cache[18] || (_cache[18] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "AI Reply", -1))
                  ], 64))
            ], 8, _hoisted_25),
            _createElementVNode("button", {
              type: "button",
              class: "social-message-composer__ai-btn social-message-composer__ai-btn--followup",
              disabled: __props.aiButtonsDisabled || __props.isGeneratingAI,
              title: __props.isGeneratingAI ? 'Generating...' : 'AI Follow-up',
              onClick: _withModifiers(handleAiFollowup, ["stop"])
            }, [
              (__props.isGeneratingAI && __props.generatingAction === 'followup')
                ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                    _cache[19] || (_cache[19] = _createElementVNode("span", { class: "social-message-composer__ai-spinner" }, null, -1)),
                    _cache[20] || (_cache[20] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "Generating...", -1))
                  ], 64))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    _createVNode(_unref(Icon), { icon: "mdi:sparkles" }),
                    _cache[21] || (_cache[21] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "AI Follow-up", -1))
                  ], 64))
            ], 8, _hoisted_26),
            _createElementVNode("button", {
              ref_key: "aiRefineButtonRef",
              ref: aiRefineButtonRef,
              type: "button",
              class: "social-message-composer__ai-btn social-message-composer__ai-btn--custom",
              disabled: __props.aiButtonsDisabled || __props.isGeneratingAI || isGeneratingAiCustom.value,
              title: isGeneratingAiCustom.value ? 'Generating...' : 'AI Refine',
              onClick: _withModifiers(toggleAiRefinePanel, ["stop"])
            }, [
              _createVNode(_unref(Icon), { icon: "mdi:sparkles" }),
              _cache[22] || (_cache[22] = _createElementVNode("span", { class: "social-message-composer__ai-btn-text" }, "AI Refine", -1))
            ], 8, _hoisted_27)
          ]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_28, [
        _createVNode(RichTextEditor, {
          ref_key: "richTextEditor",
          ref: richTextEditor,
          modelValue: messageContent.value,
          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((messageContent).value = $event)),
          height: '100%',
          toolbar: [],
          placeholder: "Type your message...",
          onEnterPressed: handleSend
        }, null, 8, ["modelValue"])
      ])
    ], 4),
    (showEmojiPopover.value)
      ? (_openBlock(), _createElementBlock("div", {
          key: 2,
          class: "social-message-composer__emoji-container",
          onClick: _withModifiers(closeEmojiPopover, ["self"])
        }, [
          _createVNode(EmojiPicker, { onSelect: handleEmojiSelect })
        ]))
      : _createCommentVNode("", true),
    (__props.showAiButtons)
      ? (_openBlock(), _createBlock(AiRefinePopover, {
          key: 3,
          show: showAiPanel.value,
          "trigger-el": aiRefineButtonRef.value,
          disabled: isGeneratingAiCustom.value,
          onGenerate: handleAiCustomizationGenerate,
          onClose: _cache[2] || (_cache[2] = ($event: any) => (showAiPanel.value = false))
        }, null, 8, ["show", "trigger-el", "disabled"]))
      : _createCommentVNode("", true),
    (__props.showAiButtons)
      ? (_openBlock(), _createBlock(AiRefinePopover, {
          key: 4,
          show: showAiComposePanel.value,
          "trigger-el": aiComposeButtonRef.value,
          disabled: __props.isGeneratingAI && __props.generatingAction === 'compose',
          title: "AI Compose",
          "generate-button-label": "Generate Message",
          "show-attachments": false,
          onGenerate: handleAiComposeGenerate,
          onClose: _cache[3] || (_cache[3] = ($event: any) => (showAiComposePanel.value = false))
        }, null, 8, ["show", "trigger-el", "disabled"]))
      : _createCommentVNode("", true),
    _createElementVNode("div", _hoisted_29, [
      _createElementVNode("div", _hoisted_30, [
        _createElementVNode("button", {
          type: "button",
          class: "social-message-composer__footer-icon-btn",
          title: "Attach files",
          onClick: _cache[4] || (_cache[4] = ($event: any) => (handleAttachment('file')))
        }, [
          _createVNode(_unref(Icon), { icon: "mdi:paperclip" })
        ]),
        (supportsVoiceMessage.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 0,
              type: "button",
              class: _normalizeClass(["social-message-composer__footer-icon-btn", { 'is-recording': isRecording.value }]),
              disabled: isVideoRecording.value,
              title: "Record voice message",
              onClick: _cache[5] || (_cache[5] = ($event: any) => (handleAttachment('voice')))
            }, [
              _createVNode(_unref(Icon), {
                icon: isRecording.value ? 'mdi:stop-circle-outline' : 'mdi:microphone-outline'
              }, null, 8, ["icon"])
            ], 10, _hoisted_31))
          : _createCommentVNode("", true),
        (isRecording.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_32, _toDisplayString(recordingTime.value), 1))
          : _createCommentVNode("", true),
        (supportsVideoMessage.value)
          ? (_openBlock(), _createElementBlock("button", {
              key: 2,
              type: "button",
              class: _normalizeClass(["social-message-composer__footer-icon-btn", { 'is-recording': isVideoRecording.value }]),
              disabled: isRecording.value,
              title: "Record video message",
              onClick: _cache[6] || (_cache[6] = ($event: any) => (isVideoRecording.value ? stopVideoRecording() : startVideoRecording()))
            }, [
              _createVNode(_unref(Icon), {
                icon: isVideoRecording.value ? 'mdi:stop-circle-outline' : 'mdi:video-outline'
              }, null, 8, ["icon"])
            ], 10, _hoisted_33))
          : _createCommentVNode("", true),
        (isVideoRecording.value)
          ? (_openBlock(), _createElementBlock("span", _hoisted_34, _toDisplayString(videoRecordingTime.value), 1))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", {
        class: "social-message-composer__footer-right",
        ref_key: "sendButtonRef",
        ref: sendButtonRef
      }, [
        _createElementVNode("div", _hoisted_35, [
          _createElementVNode("div", {
            class: "social-message-composer__popover-wrapper",
            ref_key: "linkPopoverWrapperRef",
            ref: linkPopoverWrapperRef
          }, [
            _createElementVNode("button", {
              type: "button",
              class: "social-message-composer__footer-icon-btn",
              title: "Insert link",
              onClick: _withModifiers(toggleLinkPopover, ["stop"])
            }, [
              _createVNode(_unref(Icon), { icon: "mdi:link" })
            ]),
            (showLinkPopover.value)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  ref_key: "linkPopoverRef",
                  ref: linkPopoverRef,
                  class: "social-message-composer__popover social-message-composer__popover--link",
                  style: _normalizeStyle(popoverStyle.value),
                  onClick: _cache[9] || (_cache[9] = _withModifiers(() => {}, ["stop"]))
                }, [
                  _createElementVNode("div", _hoisted_36, [
                    _createElementVNode("div", _hoisted_37, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:format-align-left",
                        class: "social-message-composer__link-field-icon"
                      }),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((linkText).value = $event)),
                        type: "text",
                        class: "social-message-composer__link-input social-message-composer__link-input--text",
                        placeholder: "Text",
                        onKeyup: [
                          _withKeys(insertLink, ["enter"]),
                          _withKeys(closeLinkPopover, ["esc"])
                        ]
                      }, null, 544), [
                        [_vModelText, linkText.value]
                      ])
                    ]),
                    _createElementVNode("div", _hoisted_38, [
                      _createVNode(_unref(Icon), {
                        icon: "mdi:link",
                        class: "social-message-composer__link-field-icon"
                      }),
                      _withDirectives(_createElementVNode("input", {
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((linkUrl).value = $event)),
                        type: "text",
                        class: "social-message-composer__link-input social-message-composer__link-input--url",
                        placeholder: "Type or paste a link",
                        onKeyup: [
                          _withKeys(insertLink, ["enter"]),
                          _withKeys(closeLinkPopover, ["esc"])
                        ],
                        ref_key: "linkInputRef",
                        ref: linkInputRef
                      }, null, 544), [
                        [_vModelText, linkUrl.value]
                      ]),
                      _createElementVNode("button", {
                        type: "button",
                        class: "social-message-composer__link-apply-btn",
                        onClick: insertLink,
                        disabled: !linkUrl.value || linkUrl.value.trim() === ''
                      }, " Apply ", 8, _hoisted_39)
                    ])
                  ])
                ], 4))
              : _createCommentVNode("", true)
          ], 512),
          _createElementVNode("button", {
            type: "button",
            class: "social-message-composer__footer-icon-btn",
            title: "Insert emoji",
            onClick: _withModifiers(toggleEmojiPopover, ["stop"])
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:emoticon-outline" })
          ])
        ]),
        _createElementVNode("div", _hoisted_40, [
          _createElementVNode("button", {
            class: _normalizeClass(["social-message-composer__send-btn", { 'social-message-composer__send-btn--disabled': !canSend.value }]),
            onClick: handleSend,
            disabled: !canSend.value
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:send" }),
            _cache[23] || (_cache[23] = _createTextVNode(" Send ", -1))
          ], 10, _hoisted_41),
          _createElementVNode("button", {
            type: "button",
            class: "social-message-composer__send-dropdown-btn",
            disabled: !canSend.value,
            onClick: _withModifiers(toggleScheduleMenu, ["stop"]),
            title: "Schedule send"
          }, [
            _createVNode(_unref(Icon), { icon: "mdi:chevron-down" })
          ], 8, _hoisted_42)
        ]),
        (showScheduleMenu.value && canSend.value)
          ? (_openBlock(), _createElementBlock("div", {
              key: 0,
              class: "social-message-composer__schedule-menu",
              onClick: _cache[10] || (_cache[10] = _withModifiers(() => {}, ["stop"]))
            }, [
              _createElementVNode("button", {
                type: "button",
                class: "social-message-composer__schedule-menu-item",
                onClick: handleScheduleMenuClick
              }, [
                _createVNode(_unref(Icon), { icon: "mdi:clock-outline" }),
                _cache[24] || (_cache[24] = _createTextVNode(" Schedule send ", -1))
              ])
            ]))
          : _createCommentVNode("", true)
      ], 512)
    ]),
    _createVNode(ScheduleSendModal, {
      show: showScheduleModal.value,
      "is-loading": isScheduling.value,
      onClose: _cache[11] || (_cache[11] = ($event: any) => (showScheduleModal.value = false)),
      onSchedule: handleScheduleSend
    }, null, 8, ["show", "is-loading"]),
    _createVNode(ActionConfirmationModal, {
      show: showAiActionConfirm.value,
      title: aiActionConfirmTitle.value,
      message: aiActionConfirmMessage.value,
      "confirm-text": "Generate",
      "cancel-text": "Cancel",
      "confirm-variant": "primary",
      "dialog-max-width": "340px",
      onConfirm: confirmAiAction,
      onClose: cancelAiAction
    }, null, 8, ["show", "title", "message"])
  ]))
}
}

})